import { SetStateAction } from 'react';

import { Dispatch } from 'redux';

import { NULL_UUID } from '../../../../constants';
import { Entity, FinancialInfo, Level, Step, Jurisdiction, FinancialInfoValue } from '../../../../models';
import { financialDataOnReceived, financialDataOnFailed } from '../../../../redux/financialData';
import HTTPService, { LambdaResponse } from '../../../../services/http';
import { Column, Row } from '../../../Table/Table.proptype';

export const getNonEditableFieldsForRow = (row: Row): string[] => {
  const isPriorYearChecked = Boolean(Number(row.priorYearFullValuationAllowance));
  const isCurrentYearChecked = Boolean(Number(row.currentYearFullValuationAllowance));
  const nonEditableFields: string[] = [];

  if (isPriorYearChecked) {
    nonEditableFields.push('begBalanceGross', 'begBalanceTaxEffected');
  }

  if (isCurrentYearChecked) {
    nonEditableFields.push('endBalanceGross', 'endBalanceTaxEffected');
  }

  return nonEditableFields;
};

export const handleCheckboxForUpdateEntityDetails = async ({
  column,
  dispatch,
  entityId,
  entityNumber,
  financialInfo,
  jurisdictionId,
  level,
  row,
  rows,
  setRows,
  step,
  value
}: {
  column: Column;
  dispatch: Dispatch<any>;
  entityId?: Entity['entityId'];
  entityNumber: string;
  financialInfo: FinancialInfo[];
  jurisdictionId?: Jurisdiction['jurisdictionId'];
  level: Level;
  row: Row;
  rows: Row[];
  setRows?: React.Dispatch<SetStateAction<Row[]>>;
  step: Step;
  value: FinancialInfoValue;
  disablePriorYearError?: boolean;
  filterAdjustmentsByRowId?: boolean;
}) => {
  const isChecked = Boolean(value);

  const targetRow = rows.find((item) => item.name === row.name);

  let updatedRow = row;

  if (targetRow && setRows) {
    const idx = rows.indexOf(targetRow);
    updatedRow = {
      ...row,
      [column.field!]: isChecked
    };

    if (column.field === 'priorYearFullValuationAllowance' && isChecked) {
      updatedRow.begBalanceGross = 0;
      updatedRow.begBalanceTaxEffected = 0;
    }

    if (column.field === 'currentYearFullValuationAllowance' && isChecked) {
      updatedRow.endBalanceGross = 0;
      updatedRow.endBalanceTaxEffected = 0;
    }

    updatedRow.nonEditableFields = getNonEditableFieldsForRow(updatedRow);

    setRows([...rows.slice(0, idx), updatedRow, ...rows.slice(idx + 1)]);
  }

  if (entityId && jurisdictionId) {
    const { createItems, updateItems } = _prepareValuationAllowanceCheckboxBatchUpdate({
      column,
      financialInfo,
      jurisdictionId,
      level,
      row: updatedRow,
      step
    });

    const uploadMetadata = {
      fileName: 'valuation-allowance-checkbox',
      fileVersion: '1',
      level,
      step
    };

    if (createItems.length > 0 || updateItems.length > 0) {
      try {
        await _updateFinancialDataBatch(entityId, createItems, updateItems, uploadMetadata);
        await _refetchFinancialDataForStep({ dispatch, entityId, entityNumber, level, step });
      } catch (error: unknown) {
        dispatch(
          financialDataOnFailed({
            error,
            rowNames: [row.name!],
            columnNames: [column.field!],
            jurisdictionId
          })
        );
        throw error;
      }
    }
  }
};

const _updateFinancialDataBatch = async (
  entityId: string,
  createItems: any[],
  updateItems: any[],
  selectionData?: any
) => {
  const { data } = await HTTPService.request<LambdaResponse<any>>({
    method: 'post',
    relativePath: `/v1/entities/${entityId}/financial-values-batch`,
    data: {
      uploadMetadata: selectionData,
      financialInfoBatch: {
        create: createItems,
        update: updateItems
      }
    }
  });

  return data;
};

const _refetchFinancialDataForStep = async ({
  dispatch,
  entityId,
  entityNumber,
  level,
  step
}: {
  dispatch: Dispatch<any>;
  entityId: Entity['entityId'];
  entityNumber: string;
  level: Level;
  step: Step;
}) => {
  try {
    const { data } = await HTTPService.request<LambdaResponse<any>>({
      method: 'get',
      relativePath: `/v1/entities/${entityId}/financial-value/${level}/${step}`
    });

    const financialInfo =
      Object.keys(data.financialInfo).length > 0 ? data.financialInfo : { [`${level}.${step}`]: [] };

    dispatch(
      financialDataOnReceived({
        tabsData: { ...financialInfo },
        entityNumber
      })
    );
  } catch (error: unknown) {
    dispatch(
      financialDataOnFailed({
        error
      })
    );
    throw error;
  }
};

const _prepareValuationAllowanceCheckboxBatchUpdate = ({
  column,
  financialInfo,
  jurisdictionId,
  level,
  row,
  step
}: {
  column: Column;
  financialInfo: FinancialInfo[];
  jurisdictionId: Jurisdiction['jurisdictionId'];
  level: Level;
  row: Row;
  step: Step;
}) => {
  const createItems: any[] = [];
  const updateItems: any[] = [];

  const fieldsToUpdate: string[] = [column.field!];

  if (column.field === 'priorYearFullValuationAllowance') {
    fieldsToUpdate.push('begBalanceGross', 'begBalanceTaxEffected');
  }

  if (column.field === 'currentYearFullValuationAllowance') {
    fieldsToUpdate.push('endBalanceGross', 'endBalanceTaxEffected');
  }

  fieldsToUpdate.forEach((fieldName) => {
    const fieldValue = Number(row[fieldName] ?? 0);
    const existingRecord = financialInfo.find(
      (item) => item.rowName === row.name && item.columnName === fieldName && item.jurisdictionId === jurisdictionId
    );

    if (existingRecord) {
      updateItems.push({
        entityFinancialsTraceId: existingRecord.entityFinancialsTraceId,
        value: fieldValue
      });
    } else {
      const createItem: any = {
        accountId: row.accountId ?? NULL_UUID,
        rowName: row.name!,
        columnName: fieldName,
        jurisdictionId,
        level,
        step,
        value: fieldValue
      };

      createItems.push(createItem);
    }
  });

  return { createItems, updateItems };
};
